/**
 * Signup intent: the role template, tests and plan a prospect chose on the
 * marketing site, carried through registration so the first assessment can
 * start from that choice instead of an empty setup.
 *
 * Pure functions only. Storage access is injected so this runs in Node tests.
 * Values are sanitised again on the server; nothing here is trusted alone.
 */

export const INTENT_STORAGE_KEY = 'tc_signup_intent'
export const INTENT_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000
export const INTENT_MAX_TESTS = 6

const SLUG = /^[a-z0-9][a-z0-9-]{0,79}$/

const cleanSlug = value => (typeof value === 'string' && SLUG.test(value) ? value : null)

const cleanSlugList = value => {
  const list = Array.isArray(value) ? value : (typeof value === 'string' ? value.split(',') : [])
  const seen = []
  for (const item of list) {
    const slug = cleanSlug(typeof item === 'string' ? item.trim() : item)
    if (slug && !seen.includes(slug)) seen.push(slug)
    if (seen.length >= INTENT_MAX_TESTS) break
  }
  return seen
}

/**
 * Build an intent from a route query such as
 * ?template=finance-quickbooks&test=quickbooks-114e8e&plan=essential
 *
 * @param {Object} query
 * @param {Number} now
 * @return {Object|null}
 */
export function intentFromQuery (query, now = Date.now()) {
  if (!query || typeof query !== 'object') return null
  const intent = {}
  const template = cleanSlug(query.template)
  if (template) intent.template = template
  const plan = cleanSlug(query.plan)
  if (plan) intent.plan = plan
  const tests = cleanSlugList(query.tests !== undefined ? query.tests : query.test)
  if (tests.length) intent.tests = tests
  if (!Object.keys(intent).length) return null
  intent.capturedAt = new Date(now).toISOString()
  return intent
}

/**
 * Normalise anything that claims to be an intent (server response, storage).
 *
 * @param {*} value
 * @param {Number} now
 * @return {Object|null}
 */
export function normaliseIntent (value, now = Date.now()) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  const intent = {}
  const template = cleanSlug(value.template)
  if (template) intent.template = template
  const plan = cleanSlug(value.plan)
  if (plan) intent.plan = plan
  const tests = cleanSlugList(value.tests)
  if (tests.length) intent.tests = tests
  if (!Object.keys(intent).length) return null
  const captured = Date.parse(value.capturedAt)
  intent.capturedAt = Number.isFinite(captured) ? new Date(captured).toISOString() : new Date(now).toISOString()
  if (now - Date.parse(intent.capturedAt) > INTENT_MAX_AGE_MS) return null
  if (value.consumedAt) intent.consumedAt = value.consumedAt
  return intent
}

/**
 * The server keeps only verified values. Prefer them, but keep a plan the
 * server did not echo (it is only used to highlight a plan).
 *
 * @param {Object|null} local
 * @param {Object|null} server
 */
export function mergeIntent (local, server, now = Date.now()) {
  const a = normaliseIntent(local, now)
  const b = normaliseIntent(server, now)
  if (!a) return b
  if (!b) return a
  const merged = { ...a, ...b }
  if (!b.plan && a.plan) merged.plan = a.plan
  merged.capturedAt = a.capturedAt < b.capturedAt ? a.capturedAt : b.capturedAt
  return normaliseIntent(merged, now)
}

/** Strip client-only fields before sending to the API. */
export function intentForServer (intent) {
  const clean = normaliseIntent(intent)
  if (!clean) return null
  const out = {}
  if (clean.template) out.template = clean.template
  if (clean.plan) out.plan = clean.plan
  if (clean.tests) out.tests = clean.tests
  return out
}

export function readIntent (storage, now = Date.now()) {
  try {
    const raw = storage && storage.getItem(INTENT_STORAGE_KEY)
    if (!raw) return null
    const intent = normaliseIntent(JSON.parse(raw), now)
    if (!intent) storage.removeItem(INTENT_STORAGE_KEY)
    return intent
  } catch (e) {
    return null
  }
}

export function writeIntent (storage, intent, now = Date.now()) {
  const clean = normaliseIntent(intent, now)
  try {
    if (!clean) { storage.removeItem(INTENT_STORAGE_KEY); return null }
    storage.setItem(INTENT_STORAGE_KEY, JSON.stringify(clean))
  } catch (e) {
    // Storage denied or full: the intent still lives in memory for this page.
  }
  return clean
}

export function clearIntent (storage) {
  try { storage.removeItem(INTENT_STORAGE_KEY) } catch (e) {}
}

/**
 * Human wording for what was chosen, using catalogue names when available.
 *
 * @param {Object|null} intent
 * @param {Object} names  { templates: {slug: name}, tests: {slug: name}, plans: {slug: name} }
 * @return {Array<string>}
 */
export function describeIntent (intent, names = {}) {
  const clean = normaliseIntent(intent)
  if (!clean) return []
  const label = (kind, slug) => (names[kind] && names[kind][slug]) || slug.replace(/-[0-9a-f]{5,6}$/, '').replace(/-/g, ' ')
  const parts = []
  if (clean.template) parts.push(label('templates', clean.template))
  if (clean.tests) parts.push(...clean.tests.map(slug => label('tests', slug)))
  if (clean.plan) parts.push(`${label('plans', clean.plan)} plan`)
  return parts
}

/**
 * The tests to propose, in order: the template battery first, then any
 * individual tests not already in it. Unknown slugs are dropped.
 *
 * @param {Object|null} intent
 * @param {Object|null} template  verified template from the API
 * @param {Array} catalogue      verified exams [{slug, name, minutes, format, includeInFreePlan}]
 * @return {Array}
 */
export function proposedExams (intent, template, catalogue = []) {
  const clean = normaliseIntent(intent)
  if (!clean) return []
  const bySlug = {}
  for (const exam of catalogue || []) if (exam && exam.slug) bySlug[exam.slug] = exam
  const out = []
  const push = exam => { if (exam && !out.some(e => e.slug === exam.slug) && out.length < INTENT_MAX_TESTS) out.push(exam) }
  if (template && clean.template === template.slug) for (const exam of template.exams || []) push(exam)
  for (const slug of clean.tests || []) push(bySlug[slug])
  return out
}

export function totalMinutes (exams) {
  return (exams || []).reduce((sum, exam) => sum + (Number.isFinite(exam.minutes) ? exam.minutes : 0), 0)
}
